// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/execution-plans/ExecutionPlanningSummaryStrip.jsx

import React, { useState } from "react";

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function displayNumber(value, fallback = "—") {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return parsed.toLocaleString();
}

function displayText(value, fallback = "—") {
  return clean(value) || fallback;
}

function titleCase(value) {
  return clean(value).replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) || "—";
}

function metricTone(key, value) {
  const parsed = Number(value);
  if (key === "high_impact_changes" && parsed > 0) return "warn";
  if (key === "approval_packages" && parsed > 0) return "info";
  if (key === "validation_ready" && parsed > 0) return "ok";
  if (key === "estimated_time_saved_minutes" && parsed > 0) return "ok";
  return "neutral";
}

const shellStyle = {
  border: "1px solid rgba(59, 130, 246, 0.26)",
  borderRadius: 20,
  padding: 18,
  background: "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 31, 62, 0.94))",
  boxShadow: "0 18px 48px rgba(15, 23, 42, 0.20)",
  color: "#e5edff",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 16,
  alignItems: "flex-start",
  flexWrap: "wrap",
  marginBottom: 14,
};

const eyebrowStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 900,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "#93c5fd",
  marginBottom: 6,
};

const titleStyle = {
  margin: 0,
  fontSize: 18,
  lineHeight: 1.2,
  color: "#f8fbff",
};

const bodyStyle = {
  margin: "6px 0 0",
  color: "#cbd5e1",
  fontSize: 13,
  lineHeight: 1.45,
  maxWidth: 760,
};

const buttonBaseStyle = {
  appearance: "none",
  WebkitAppearance: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 36,
  padding: "8px 14px",
  borderRadius: 999,
  border: "1px solid rgba(148, 163, 184, 0.32)",
  background: "linear-gradient(180deg, rgba(15, 23, 42, 0.96), rgba(15, 28, 56, 0.94))",
  color: "#eaf0ff",
  fontSize: 13,
  fontWeight: 850,
  lineHeight: 1,
  whiteSpace: "nowrap",
  cursor: "pointer",
  boxShadow: "0 8px 18px rgba(0, 0, 0, 0.18)",
  transition:
    "transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease, color 0.16s ease, opacity 0.16s ease",
};

const buttonHoverStyle = {
  transform: "translateY(-1px)",
  border: "1px solid rgba(96, 165, 250, 0.72)",
  background: "linear-gradient(180deg, rgba(96, 165, 250, 0.24), rgba(30, 64, 175, 0.24))",
  color: "#ffffff",
  boxShadow: "0 0 0 3px rgba(96, 165, 250, 0.16), 0 12px 24px rgba(0, 0, 0, 0.28)",
};

const buttonLoadingStyle = {
  border: "1px solid rgba(96, 165, 250, 0.62)",
  background: "linear-gradient(180deg, rgba(96, 165, 250, 0.28), rgba(30, 64, 175, 0.30))",
  color: "#ffffff",
  cursor: "wait",
  opacity: 0.92,
};

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
  gap: 10,
  marginTop: 12,
};

const metricBaseStyle = {
  border: "1px solid rgba(148, 163, 184, 0.20)",
  borderRadius: 16,
  padding: "12px 13px",
  background: "rgba(15, 23, 42, 0.58)",
};

const metricToneStyle = {
  neutral: {},
  info: { borderColor: "rgba(96, 165, 250, 0.42)", background: "rgba(30, 64, 175, 0.22)" },
  ok: { borderColor: "rgba(52, 211, 153, 0.40)", background: "rgba(6, 95, 70, 0.20)" },
  warn: { borderColor: "rgba(251, 191, 36, 0.42)", background: "rgba(120, 53, 15, 0.20)" },
};

const metricLabelStyle = {
  display: "block",
  fontSize: 11,
  color: "#aebddb",
  fontWeight: 850,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const metricValueStyle = {
  display: "block",
  marginTop: 6,
  fontSize: 24,
  lineHeight: 1,
  color: "#ffffff",
  fontWeight: 950,
};

const metricFootnoteStyle = {
  display: "block",
  marginTop: 6,
  fontSize: 11,
  color: "#b6c3d8",
};

const lowerGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 10,
  marginTop: 12,
};

const detailCardStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 16,
  padding: 13,
  background: "rgba(2, 6, 23, 0.26)",
};

const detailTitleStyle = {
  display: "block",
  color: "#dbeafe",
  fontSize: 12,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: 8,
};

const listStyle = {
  margin: 0,
  paddingLeft: 18,
  color: "#cbd5e1",
  fontSize: 12,
  lineHeight: 1.45,
};

const guardrailStyle = {
  marginTop: 12,
  padding: "10px 12px",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 14,
  color: "#bfdbfe",
  fontSize: 12,
  background: "rgba(15, 23, 42, 0.42)",
};

function Metric({ label, value, footnote, toneKey }) {
  const tone = metricTone(toneKey, value);

  return (
    <div style={{ ...metricBaseStyle, ...(metricToneStyle[tone] || {}) }}>
      <span style={metricLabelStyle}>{label}</span>
      <strong style={metricValueStyle}>{displayNumber(value)}</strong>
      {footnote ? <small style={metricFootnoteStyle}>{footnote}</small> : null}
    </div>
  );
}

function SummaryRefreshButton({ loading, onRefresh }) {
  const [hovered, setHovered] = useState(false);

  const activeLoading = Boolean(loading);

  return (
    <button
      type="button"
      style={{
        ...buttonBaseStyle,
        ...(hovered && !activeLoading ? buttonHoverStyle : null),
        ...(activeLoading ? buttonLoadingStyle : null),
      }}
      onClick={onRefresh}
      disabled={activeLoading}
      aria-busy={activeLoading}
      aria-live="polite"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      {activeLoading ? "Refreshing…" : "Refresh Summary"}
    </button>
  );
}

function RecommendedWindows({ windows }) {
  const entries = Object.entries(windows || {}).filter(([key, value]) => clean(key) && clean(value));

  if (!entries.length) {
    return <p style={{ ...bodyStyle, margin: 0 }}>No recommended execution windows are available yet.</p>;
  }

  return (
    <ul style={listStyle}>
      {entries.map(([key, value]) => (
        <li key={key}>
          <strong>{titleCase(key)}:</strong> {displayText(value)}
        </li>
      ))}
    </ul>
  );
}

export default function ExecutionPlanningSummaryStrip({
  context = null,
  loading = false,
  error = "",
  onRefresh,
}) {
  const summary = context?.summary || {};
  const runtimeVersion = displayText(
    context?.runtime_version || context?.execution_planning_version,
    "execution_planning.v1"
  );
  const guardrails = context?.guardrails || {};
  const guardrailCount = Object.values(guardrails).filter((value) => value === false).length;

  return (
    <section style={shellStyle} aria-label="Executive execution planning summary">
      <div style={headerStyle}>
        <div>
          <span style={eyebrowStyle}>Execution Planning · Executive Summary</span>
          <h3 style={titleStyle}>Plan before execution</h3>
          <p style={bodyStyle}>
            OASYS is generating advisory execution packages with change windows, rollback readiness,
            approval workload, and operational impact before any operator action is taken.
          </p>
        </div>

        {typeof onRefresh === "function" ? (
          <SummaryRefreshButton loading={loading} onRefresh={onRefresh} />
        ) : null}
      </div>

      {error ? (
        <div style={guardrailStyle}>
          Execution planning summary could not be loaded: {error}
        </div>
      ) : null}

      <div style={metricGridStyle}>
        <Metric label="Planned Work" value={summary.planned_work} footnote="Generated task plans" toneKey="planned_work" />
        <Metric label="High Impact" value={summary.high_impact_changes} footnote="Change-risk focus" toneKey="high_impact_changes" />
        <Metric label="Approval Packages" value={summary.approval_packages} footnote="Ready for review" toneKey="approval_packages" />
        <Metric label="Validation Ready" value={summary.validation_ready} footnote="Can verify after action" toneKey="validation_ready" />
        <Metric label="Time Saved" value={summary.estimated_time_saved_minutes} footnote="Minutes estimated" toneKey="estimated_time_saved_minutes" />
      </div>

      <div style={lowerGridStyle}>
        <div style={detailCardStyle}>
          <span style={detailTitleStyle}>Recommended Windows</span>
          <RecommendedWindows windows={summary.recommended_windows} />
        </div>

        <div style={detailCardStyle}>
          <span style={detailTitleStyle}>Readiness</span>
          <ul style={listStyle}>
            <li><strong>Rollback:</strong> {displayText(summary.rollback_readiness)}</li>
            <li><strong>Operational:</strong> {displayText(summary.operational_readiness)}</li>
            <li><strong>Blocked / dependency review:</strong> {displayNumber(summary.blocked_or_dependency_review)}</li>
          </ul>
        </div>

        <div style={detailCardStyle}>
          <span style={detailTitleStyle}>Source Coverage</span>
          <ul style={listStyle}>
            <li><strong>Runtime:</strong> {runtimeVersion}</li>
            <li><strong>Remediation tasks:</strong> {displayNumber(summary.source_summary?.remediation_tasks)}</li>
            <li><strong>Analytics available:</strong> {summary.source_summary?.analytics_available ? "Yes" : "No"}</li>
          </ul>
        </div>
      </div>

      <div style={guardrailStyle}>
        Advisory only · No autonomous execution · No cloud mutation · No approval execution · No scheduling.
        {guardrailCount ? ` ${guardrailCount} guardrails explicitly locked.` : ""}
      </div>
    </section>
  );
}